import { RouteComponentProps } from 'react-router-dom';
import uniqBy from 'lodash/uniqBy';
import qs from 'query-string';
import { ISelectOptions } from 'src/components/ui/Select/createSelect.types';
import { AppDispatch } from 'src/store';
import { IQueryParams } from 'src/types';
import { convertFilterToArray } from 'src/utils/convertFilter';
import { removeEmpty } from 'src/utils/removeEmpty';
import { createAction } from '@reduxjs/toolkit';

import { IMerchantAccount } from '../connectedProviders/accountsPayment/types';
import { ICreateForm, IPaymentForm, IPaymentRoutingData, IPaymentSystemGroupForm } from '../paymentRouting/types';

import {
  createPayoutFormDirectRequest,
  createPayoutFormRoutingRequest,
  fetchPayoutMerchantAccountByFormRequest,
  fetchPayoutMerchantAccountByIdRequest,
  fetchPayoutProvidersSearchRequest,
  fetchPayoutRouteRequest,
  fetchPayoutRoutingRequest,
  fetchSystemGroupByIdRequest,
  fetchSystemGroupsRequest,
  removeImagePayoutFormRoutingRequest,
  updateImagePayoutFormRoutingRequest,
  updatePayoutFormDirectRequest,
  updatePayoutFormRoutingRequest,
} from './requestHandlers';

const defaultParams = {
  page: '0',
  size: '25',
  sort: 'id,asc',
};

export const loadPayoutRoutingStart = createAction('payout_routing/routing/load/start');

export const loadPayoutRoutingListSuccess = createAction<IPaymentRoutingData>('payout_routing/routing/load/success');

export const loadPayoutRouteStart = createAction('payout_routing/route/load/start');

export const loadPayoutRouteListSuccess = createAction<Partial<IPaymentForm>>('payout_routing/route/load/success');

export const resetPayoutRouteItem = createAction('Payout_routing/route/reset');

export const loadPayoutRoutingRequest = (
  history: RouteComponentProps['history'],
  params: IQueryParams = {},
  isInitialLoading?: boolean,
  type?: string,
) => {
  return async (dispatch: AppDispatch): Promise<void> => {
    if (isInitialLoading) {
      dispatch(loadPayoutRoutingStart());
    }
    const initialParams = qs.parse(history.location.search);
    const mergedParams = type === 'reset' ? defaultParams : removeEmpty({ archived: false, ...defaultParams, ...initialParams, ...params });

    mergedParams.systemGroupFormIds = convertFilterToArray(mergedParams.systemGroupFormIds);
    mergedParams.siteIds = convertFilterToArray(mergedParams.siteIds);
    mergedParams.currencies = convertFilterToArray(mergedParams.currencies);
    mergedParams.providerNames = convertFilterToArray(mergedParams.providerNames);
    mergedParams.merchantAccountIds = convertFilterToArray(mergedParams.merchantAccountIds);
    mergedParams.names = convertFilterToArray(mergedParams.names);
    mergedParams.tags = convertFilterToArray(mergedParams.tags);

    const data = await fetchPayoutRoutingRequest(mergedParams);

    if (data) {
      dispatch(loadPayoutRoutingListSuccess(data));
      history.replace({
        search: qs.stringify(mergedParams, { skipNull: true }),
      });
    }
  };
};

export const loadPayoutRouteRequest = (id: string, isInitialLoading?: boolean) => {
  return async (dispatch: AppDispatch): Promise<void> => {
    if (isInitialLoading) {
      dispatch(loadPayoutRouteStart());
    }
    const data = await fetchPayoutRouteRequest(id);
    if (data) {
      dispatch(loadPayoutRouteListSuccess(data));
    }
  };
};

export const fetchSystemGroups = async (params: IQueryParams): Promise<IPaymentSystemGroupForm[]> => fetchSystemGroupsRequest(params);

export const fetchSystemGroupsOptions = async (params: IQueryParams, initialId?: string): Promise<IPaymentSystemGroupForm[]> => {
  const res = await fetchSystemGroupsRequest(params);
  if (initialId) {
    const initialValue = await fetchSystemGroupByIdRequest(initialId);
    res.push(initialValue);
  }

  return uniqBy(res, 'id');
};

export const fetchMerchantAccountOptions = async (id?: string, initialMerchantId?: string): Promise<IMerchantAccount[]> => {
  const res = await fetchPayoutMerchantAccountByFormRequest(id);
  if (initialMerchantId) {
    const initialValue = await fetchPayoutMerchantAccountByIdRequest(initialMerchantId);
    res.push(initialValue);
  }

  return uniqBy(res, 'id');
};

export const fetchPayoutProvidersSearch = async (): Promise<ISelectOptions | []> => fetchPayoutProvidersSearchRequest();

export const createPayoutFormDirect = async ({ res }: ICreateForm): Promise<Partial<IPaymentForm>> => {
  const data = await createPayoutFormDirectRequest(res);

  return data;
};
export const updatePayoutFormDirect = async ({ res, logo }: ICreateForm): Promise<Partial<IPaymentForm>> => {
  if (logo) {
    await updateImagePayoutFormRoutingRequest(res?.id, logo);
  }
  return updatePayoutFormDirectRequest(res);
};
export const createPayoutFormRouting = ({ res }: ICreateForm): Promise<Partial<IPaymentForm>> => createPayoutFormRoutingRequest(res);
export const updatePayoutFormRouting = async ({ res, logo }: ICreateForm): Promise<Partial<IPaymentForm>> => {
  if (logo) {
    await updateImagePayoutFormRoutingRequest(res?.id, logo);
  }
  return updatePayoutFormRoutingRequest(res);
};

export const removeImagePayoutFormRouting = (id: string): Promise<void> => removeImagePayoutFormRoutingRequest(id);
